/**
 * Local fetch-based service for the XEPPT Payouts API.
 * Does NOT use the xeppt-sdk — the payouts endpoint is portal-JWT-gated
 * and not part of the published SDK.
 *
 * AUTH: Bearer access token from localStorage 'client-metadata', same
 * pattern as webhook_service.ts and sandbox_service.ts.
 *
 * API CONTRACT:
 *   GET /account/payouts?page=1&page_size=30&environment=test|live[&date_from=&date_to=&status[]=...]
 *   → { payouts: Payout[]; total: number; has_more?: boolean }
 *
 *   GET /account/payouts/{id}
 *   → Payout
 *
 * ENVIRONMENT: `list()` takes an optional `environment` filter (from the
 * dev-portal's Sandbox/Live pill, via useEnvironmentContext) and sends it as
 * ?environment=<value> when passed, same convention as webhook_service.ts.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type PayoutStatus = 'pending' | 'succeeded' | 'failed';

export interface Payout {
    id: string;
    /** Stable per-merchant customer identifier (anonymous, never contains PII). */
    scoped_id: string;
    amount_cents: number;
    currency: string;
    status: PayoutStatus;
    created_at: string;
}

export interface ListPayoutsResponse {
    payouts: Payout[];
    total: number;
    has_more?: boolean;
}

export interface ListPayoutsParams {
    page?: number;
    page_size?: number;
    date_from?: string;
    date_to?: string;
    status?: PayoutStatus[];
    environment?: 'test' | 'live';
}

// ─── Error class ────────────────────────────────────────────────────────────

export class PayoutApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'PayoutApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new PayoutApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        code
    );
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        await throwOnError(response);
    }
    return response.json() as Promise<T>;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const apiPayoutService = {
    /**
     * GET /account/payouts — page-based list of payouts for the authenticated
     * merchant. Supports optional date range and status filters.
     */
    async list(params?: ListPayoutsParams): Promise<ListPayoutsResponse> {
        const qs = new URLSearchParams();
        if (params?.page != null) qs.set('page', String(params.page));
        if (params?.page_size != null) qs.set('page_size', String(params.page_size));
        if (params?.date_from) qs.set('date_from', params.date_from);
        if (params?.date_to) qs.set('date_to', params.date_to);
        params?.status?.forEach((s) => qs.append('status[]', s));
        if (params?.environment) qs.set('environment', params.environment);
        const response = await fetch(`${BACKEND_URL}/account/payouts?${qs.toString()}`, {
            headers: authHeaders()
        });
        return parseResponse<ListPayoutsResponse>(response);
    },

    /** GET /account/payouts/{id} — single payout detail. */
    async get(id: string): Promise<Payout> {
        const response = await fetch(`${BACKEND_URL}/account/payouts/${id}`, {
            headers: authHeaders()
        });
        return parseResponse<Payout>(response);
    }
};
